<template>
  <div class="pl-root">
    <div class="pl-grain" aria-hidden="true"></div>

    <!-- ─── NAV ─── -->
    <nav class="pl-nav" :class="{ 'pl-nav--solid': scrolled }">
      <div class="pl-nav-inner">
        <router-link to="/" class="pl-brand">
          <span class="pl-brand-mark"><img src="../assets/Images/ecoframe_logo.png" alt="ECOFRAME" /></span>
          <span class="pl-brand-text">
            <span class="pl-brand-name">ECOFRAME</span>
            <span class="pl-brand-sub">Kerala Forest Department</span>
          </span>
        </router-link>
        <div class="pl-nav-links">
          <a href="#craft" class="pl-nav-link">The Portal</a>
          <a href="#schedule" class="pl-nav-link">Process</a>
          <a href="#genres" class="pl-nav-link">Categories</a>
          <span class="pl-nav-sep" aria-hidden="true"></span>
          <router-link to="/register" class="pl-nav-link">Register</router-link>
          <router-link to="/login" class="pl-btn pl-btn--sm">
            Sign in
            <v-icon small color="#03140a">mdi-arrow-right</v-icon>
          </router-link>
        </div>
      </div>
    </nav>

    <!-- ─── HERO ─── -->
    <section class="pl-hero">
      <div class="pl-hero-media">
        <img src="../assets/Images/ibimage12.jpg" alt="A herd of elephants in a Kerala forest clearing" class="pl-hero-img" />
      </div>
      <div class="pl-hero-shade"></div>
      <div class="pl-hero-orb pl-hero-orb--1" aria-hidden="true"></div>
      <div class="pl-hero-orb pl-hero-orb--2" aria-hidden="true"></div>
      <div class="pl-letterbox pl-letterbox--top" aria-hidden="true"></div>
      <div class="pl-letterbox pl-letterbox--bottom" aria-hidden="true"></div>

      <!-- Discreet viewfinder -->
      <div class="pl-vf" aria-hidden="true">
        <span class="pl-vf-c pl-vf-c--tl"></span>
        <span class="pl-vf-c pl-vf-c--tr"></span>
        <span class="pl-vf-c pl-vf-c--bl"></span>
        <span class="pl-vf-c pl-vf-c--br"></span>
        <span class="pl-vf-rec"><i></i> REC</span>
        <span class="pl-vf-tc">{{ timecode }}</span>
      </div>

      <div class="pl-hero-inner">
        <div class="pl-hero-copy">
          <div class="pl-eyebrow">
            <span class="pl-eyebrow-line"></span>
            Official Film Shooting Portal · Government of Kerala
          </div>
          <h1 class="pl-title">
            Where the forest<br />
            becomes <em>your set.</em>
          </h1>
          <p class="pl-lead">
            Permission to film, document and photograph in Kerala's protected forests — applied for, reviewed and licensed entirely online.
          </p>
          <div class="pl-cta">
            <router-link to="/login" class="pl-btn">
              Apply for a permit
              <v-icon small color="#03140a">mdi-arrow-right</v-icon>
            </router-link>
            <a href="#schedule" class="pl-btn pl-btn--glass">
              <v-icon small color="#e9f5ee">mdi-play</v-icon>
              How it works
            </a>
          </div>
          <dl class="pl-trust">
            <div v-for="t in trust" :key="t.label">
              <dt>{{ t.value }}</dt>
              <dd>{{ t.label }}</dd>
            </div>
          </dl>
        </div>

        <!-- Permit pass -->
        <aside class="pl-pass d-none d-md-block" aria-label="Sample shooting permit">
          <div class="pl-pass-head">
            <span class="pl-pass-emblem"><img src="../assets/Images/blacklogo.png" alt="" /></span>
            <span>
              <span class="pl-pass-kicker">Shooting Permit</span>
              <span class="pl-pass-org">Kerala Forest Department</span>
            </span>
            <span class="pl-pass-live"><i></i> Online</span>
          </div>
          <div class="pl-pass-title">Your production, <em>approved.</em></div>
          <div class="pl-pass-grid">
            <div><span>Location</span><b>Kerala forests</b></div>
            <div><span>Categories</span><b>{{ categories.length }}</b></div>
            <div><span>Payment</span><b>e-Treasury</b></div>
            <div><span>Licence</span><b>Digital</b></div>
          </div>
          <div class="pl-pass-perf" aria-hidden="true"></div>
          <ol class="pl-pass-steps">
            <li v-for="(s, i) in schedule" :key="s.title" :class="{ 'is-done': i === 0 }">
              <span class="pl-pass-dot"></span>
              {{ s.title }}
            </li>
          </ol>
        </aside>
      </div>

      <a href="#craft" class="pl-scroll" aria-label="Scroll down">
        <span></span>
      </a>
    </section>

    <!-- ─── MARQUEE ─── -->
    <div class="pl-marquee" aria-label="Shooting categories">
      <div class="pl-marquee-track">
        <span v-for="(c, i) in marquee" :key="i" class="pl-marquee-item">
          {{ c }}<i aria-hidden="true">✦</i>
        </span>
      </div>
    </div>

    <!-- ─── THE PORTAL (features, editorial) ─── -->
    <section class="pl-section" id="craft">
      <div class="pl-inner pl-craft">
        <div class="pl-craft-head reveal">
          <span class="pl-tag">The Portal</span>
          <h2 class="pl-h2">Every step of a forest shoot, <em>in one place.</em></h2>
          <p class="pl-sub">Built with the Kerala Forest Department so productions spend less time on paperwork and more time behind the camera.</p>
          <router-link to="/register" class="pl-link">
            Create your account
            <v-icon small color="#4ade80">mdi-arrow-right</v-icon>
          </router-link>
        </div>
        <ol class="pl-craft-list">
          <li v-for="(f, i) in features" :key="f.title" class="pl-craft-item reveal" :style="{ transitionDelay: i * 60 + 'ms' }">
            <span class="pl-craft-no">{{ String(i + 1).padStart(2, "0") }}</span>
            <span class="pl-craft-icon"><v-icon color="#4ade80" size="22">{{ f.icon }}</v-icon></span>
            <span class="pl-craft-body">
              <span class="pl-craft-title">{{ f.title }}</span>
              <span class="pl-craft-desc">{{ f.desc }}</span>
            </span>
          </li>
        </ol>
      </div>
    </section>

    <!-- ─── PROCESS ─── -->
    <section class="pl-section pl-section--tint" id="schedule">
      <div class="pl-inner">
        <div class="pl-head reveal">
          <span class="pl-tag">Production Schedule</span>
          <h2 class="pl-h2">From application to licence, <em>in four scenes.</em></h2>
        </div>
        <ol class="pl-steps">
          <li v-for="(s, i) in schedule" :key="s.title" class="pl-step reveal" :style="{ transitionDelay: i * 110 + 'ms' }">
            <span class="pl-step-no">Scene {{ i + 1 }}</span>
            <span class="pl-step-ring"><v-icon color="#4ade80" size="24">{{ s.icon }}</v-icon></span>
            <h3 class="pl-step-title">{{ s.title }}</h3>
            <p class="pl-step-desc">{{ s.desc }}</p>
          </li>
        </ol>
      </div>
    </section>

    <!-- ─── CATEGORIES ─── -->
    <section class="pl-section" id="genres">
      <div class="pl-inner">
        <div class="pl-head reveal">
          <span class="pl-tag">Now Showing</span>
          <h2 class="pl-h2">Permissions for every <em>kind of production.</em></h2>
        </div>
        <div class="pl-posters">
          <article v-for="(g, i) in genres" :key="g.name" class="pl-poster reveal" :style="{ transitionDelay: i * 80 + 'ms' }">
            <div class="pl-poster-art" :style="{ background: g.art }">
              <span class="pl-poster-badge">{{ g.badge }}</span>
              <v-icon size="44" color="rgba(255,255,255,0.88)">{{ g.icon }}</v-icon>
              <span class="pl-poster-index">{{ String(i + 1).padStart(2, "0") }}</span>
            </div>
            <div class="pl-poster-body">
              <h3 class="pl-poster-title">{{ g.name }}</h3>
              <p class="pl-poster-desc">{{ g.desc }}</p>
              <div class="pl-poster-tags">
                <span v-for="t in g.tags" :key="t">{{ t }}</span>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- ─── STATEMENT ─── -->
    <section class="pl-section pl-section--tint">
      <div class="pl-inner pl-about">
        <figure class="pl-about-media reveal">
          <div class="pl-about-frame">
            <img src="../assets/Images/film.jpg" alt="Film camera and clapperboard on set" />
          </div>
          <figcaption class="pl-about-cap">
            <v-icon small color="#03140a">mdi-shield-check</v-icon>
            Official Kerala Forest Department portal
          </figcaption>
        </figure>
        <div class="pl-about-text reveal">
          <span class="pl-tag">Behind the Scenes</span>
          <blockquote class="pl-quote">
            “Creative work, with the forest in mind.”
          </blockquote>
          <p class="pl-sub pl-sub--left">
            Every application is reviewed by the division office and inspected by the range officer of the location, so productions go ahead without harming the forests they film.
          </p>
          <ul class="pl-points">
            <li v-for="p in points" :key="p.title">
              <span class="pl-point-mark"></span>
              <span><strong>{{ p.title }}</strong> {{ p.desc }}</span>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- ─── CTA ─── -->
    <section class="pl-final">
      <img src="../assets/Images/ibimage12.jpg" alt="" class="pl-final-img" />
      <div class="pl-final-shade"></div>
      <div class="pl-final-inner reveal">
        <span class="pl-tag pl-tag--light">Ready when you are</span>
        <h2 class="pl-final-title">Bring your story <em>to the forest.</em></h2>
        <p class="pl-final-sub">Create an account, plan your shoot and send your application to the division office today.</p>
        <div class="pl-cta pl-cta--center">
          <router-link to="/register" class="pl-btn">
            Create an account
            <v-icon small color="#03140a">mdi-arrow-right</v-icon>
          </router-link>
          <router-link to="/login" class="pl-btn pl-btn--glass">Sign in</router-link>
        </div>
      </div>
    </section>

    <!-- ─── CREDITS ─── -->
    <footer class="pl-credits">
      <div class="pl-inner">
        <div class="pl-credits-brand">
          <span class="pl-brand-mark"><img src="../assets/Images/ecoframe_logo.png" alt="" /></span>
          <span class="pl-brand-name">ECOFRAME</span>
        </div>
        <dl class="pl-credits-roll">
          <div><dt>Presented by</dt><dd>Kerala Forest Department</dd></div>
          <div><dt>Developed by</dt><dd>Leopard Tech Labs Pvt. Ltd.</dd></div>
        </dl>
        <div class="pl-credits-links">
          <router-link to="/login">Sign in</router-link>
          <router-link to="/register">Register</router-link>
          <a href="#craft">The Portal</a>
          <a href="#schedule">Process</a>
        </div>
        <p class="pl-credits-copy">© {{ year }} Kerala Forest Department, Government of Kerala. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script>
// Application categories offered on the booking form (user/BookNatureCamp.vue).
const CATEGORIES = [
  "Commercial Film",
  "Documentary",
  "TV Serial",
  "Educational Films/ Photography-Private parties-non-commercial",
  "Educational Films/ Photography- Government agency-non-commercial",
  "Educational film - commercial purpose",
  "Commercial Photography",
  "Non-Commercial photographs/videos",
];

const MARQUEE = ["Commercial Film", "Documentary", "TV Serial", "Educational Film", "Commercial Photography", "Non-Commercial Photos & Videos"];

export default {
  name: "LandingPage",
  data() {
    return {
      scrolled: false,
      frame: 0,
      categories: CATEGORIES,
      // Twice, so the marquee loops seamlessly.
      marquee: MARQUEE.concat(MARQUEE),
      trust: [
        { value: String(CATEGORIES.length), label: "Shoot categories" },
        { value: "100%", label: "Online process" },
        { value: "e-Treasury", label: "Secure payment" },
      ],
      features: [
        { icon: "mdi-file-document-edit-outline", title: "Apply online", desc: "Locations, shooting dates, crew, vehicles, animals involved and documents — in one guided form." },
        { icon: "mdi-account-tie-outline", title: "Division review", desc: "The Section Clerk, Superintendent and DFO of the division review the application in turn." },
        { icon: "mdi-map-marker-radius-outline", title: "Location reports", desc: "Range officers inspect each location and upload their reports and mahazars online." },
        { icon: "mdi-bank-outline", title: "e-Treasury payment", desc: "Pay the shooting fee and security deposit securely; refunds are tracked as well." },
        { icon: "mdi-file-certificate-outline", title: "Digital licence", desc: "Agreement, proceedings and licence are generated and shared once the shoot is approved." },
        { icon: "mdi-timeline-clock-outline", title: "Live tracking", desc: "See which desk has your application and every action taken on it, as it happens." },
      ],
      schedule: [
        { icon: "mdi-account-plus-outline", title: "Apply", desc: "Register and submit your shooting application with documents." },
        { icon: "mdi-account-check-outline", title: "Division review", desc: "The division office reviews the application and fixes the fees." },
        { icon: "mdi-cash-check", title: "Pay & sign", desc: "Pay through e-Treasury and execute the agreement." },
        { icon: "mdi-file-certificate-outline", title: "Licence & shoot", desc: "Receive your licence; the range officer reports on the site." },
      ],
      genres: [
        { name: "Films & TV Serials", badge: "Licence", desc: "Commercial feature films, ad films and television serials.", icon: "mdi-movie-open-outline", art: "radial-gradient(circle at 30% 20%, #1f7a44 0%, #0b3a20 55%, #03140a 100%)", tags: ["Commercial Film", "TV Serial"] },
        { name: "Documentaries", badge: "Licence", desc: "Nature, wildlife and conservation documentaries.", icon: "mdi-filmstrip", art: "radial-gradient(circle at 70% 25%, #2a6b4f 0%, #0d3325 55%, #03140a 100%)", tags: ["Wildlife", "Nature"] },
        { name: "Photography", badge: "Permit", desc: "Commercial photography and non-commercial photos or videos.", icon: "mdi-camera-outline", art: "radial-gradient(circle at 30% 30%, #4d6b1f 0%, #1f3209 55%, #03140a 100%)", tags: ["Commercial", "Non-Commercial"] },
        { name: "Educational Films", badge: "Permit", desc: "Educational films and photography by government or private parties.", icon: "mdi-school-outline", art: "radial-gradient(circle at 65% 30%, #1e6a63 0%, #0b302d 55%, #03140a 100%)", tags: ["Government", "Private"] },
      ],
      points: [
        { title: "Official —", desc: "operated by the Kerala Forest Department." },
        { title: "Role-based —", desc: "applicants, range officers, division staff and admins each see their own desk." },
        { title: "Transparent —", desc: "a full timeline of every forward, return and approval." },
        { title: "Flexible —", desc: "request extensions or cancel online." },
      ],
    };
  },
  computed: {
    year() {
      return new Date().getFullYear();
    },
    // Viewfinder timecode HH:MM:SS:FF at 24 fps.
    timecode() {
      const f = this.frame;
      const pad = (n) => String(n).padStart(2, "0");
      const secs = Math.floor(f / 24);
      return [Math.floor(secs / 3600), Math.floor(secs / 60) % 60, secs % 60, f % 24].map(pad).join(":");
    },
  },
  mounted() {
    window.addEventListener("scroll", this.onScroll, { passive: true });
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!reduce) this.timer = setInterval(() => (this.frame += 1), 1000 / 24);

    const els = this.$el.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("reveal--in"));
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("reveal--in");
            this.observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => this.observer.observe(el));
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
    clearInterval(this.timer);
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    onScroll() {
      this.scrolled = window.scrollY > 30;
    },
  },
};
</script>

<style scoped>

.pl-root {
  --bg: #03100a;
  --bg-2: #061710;
  --card: rgba(255, 255, 255, 0.035);
  --line: rgba(233, 245, 238, 0.1);
  --line-strong: rgba(233, 245, 238, 0.18);
  --emerald: #22c55e;
  --emerald-soft: #4ade80;
  --gold: #d6b97b;
  --ink: #e9f5ee;
  --muted: #9db3a6;
  position: relative;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  background: var(--bg);
  color: var(--ink);
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
}
.pl-root a { text-decoration: none; }
.pl-root em { font-style: italic; color: var(--emerald-soft); }

/* Fine film grain over the whole page */
.pl-grain {
  position: fixed; inset: 0; z-index: 60; pointer-events: none; opacity: 0.07; mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
}

/* ─── Buttons ─── */
.pl-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 10px;
  padding: 15px 28px; border-radius: 999px;
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 50%, #16a34a 100%);
  color: #03140a !important; font-size: 15px; font-weight: 700; letter-spacing: 0.3px;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.5) inset, 0 14px 34px rgba(34, 197, 94, 0.35);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  position: relative; overflow: hidden;
}
.pl-btn::after { content: ""; position: absolute; inset: 0; border-radius: 999px; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent); transform: translateX(-100%); transition: transform 0.55s ease; }
.pl-btn:hover::after { transform: translateX(100%); }
.pl-btn:hover { transform: translateY(-2px); box-shadow: 0 1px 0 rgba(255, 255, 255, 0.5) inset, 0 22px 50px rgba(34, 197, 94, 0.5), 0 0 30px rgba(74,222,128,0.22); }
.pl-btn .v-icon { transition: transform 0.25s ease; }
.pl-btn:hover .v-icon { transform: translateX(4px); }
.pl-btn--glass {
  background: rgba(255, 255, 255, 0.07); color: var(--ink) !important;
  border: 1px solid var(--line-strong);
  backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
  box-shadow: none;
}
.pl-btn--glass::after { display: none; }
.pl-btn--glass:hover { background: rgba(255, 255, 255, 0.14); box-shadow: 0 0 24px rgba(74,222,128,0.1); border-color: rgba(74,222,128,0.3); transform: translateY(-2px); }
.pl-btn--sm { padding: 10px 18px; font-size: 13.5px; }

.pl-link { display: inline-flex; align-items: center; gap: 8px; color: var(--emerald-soft); font-weight: 600; font-size: 15px; border-bottom: 1px solid rgba(74, 222, 128, 0.35); padding-bottom: 4px; }
.pl-link:hover { border-bottom-color: var(--emerald-soft); }

/* ─── Nav ─── */
.pl-nav { position: fixed; inset: 0 0 auto 0; z-index: 50; transition: background 0.4s ease, border-color 0.4s ease; border-bottom: 1px solid transparent; }
.pl-nav--solid {
  background: rgba(3, 16, 10, 0.85); border-bottom-color: rgba(214, 185, 123, 0.25);
  backdrop-filter: blur(24px) saturate(180%); -webkit-backdrop-filter: blur(24px) saturate(180%);
  box-shadow: 0 1px 0 rgba(74,222,128,0.08), 0 8px 32px rgba(0,0,0,0.4);
}
.pl-nav-inner { max-width: 1760px; margin: 0 auto; height: 78px; padding: 0 clamp(16px, 4vw, 48px); display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.pl-brand { display: flex; align-items: center; gap: 12px; }
.pl-brand-mark { width: 42px; height: 42px; border-radius: 12px; background: #fff; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 0 0 1px rgba(214, 185, 123, 0.45), 0 8px 20px rgba(0, 0, 0, 0.35); }
.pl-brand-mark img { width: 86%; height: 86%; object-fit: contain; }
.pl-brand-text { display: flex; flex-direction: column; line-height: 1.15; }
.pl-brand-name { font-size: 15px; font-weight: 700; letter-spacing: 4px; color: var(--ink); }
.pl-brand-sub { font-size: 10.5px; font-weight: 500; letter-spacing: 1.6px; text-transform: uppercase; color: var(--muted); }
.pl-nav-links { display: flex; align-items: center; gap: 30px; }
.pl-nav-link { color: rgba(233, 245, 238, 0.72); font-size: 14px; font-weight: 500; transition: color 0.2s; }
.pl-nav-link:hover { color: var(--ink); }
.pl-nav-sep { width: 1px; height: 18px; background: var(--line-strong); }

/* ─── Hero ─── */
.pl-hero { position: relative; min-height: 100vh; display: flex; align-items: center; overflow: hidden; padding: 120px 0 96px; }
.pl-hero-media { position: absolute; inset: 0; }
.pl-hero-img { width: 100%; height: 100%; object-fit: cover; animation: plDrift 30s ease-in-out infinite alternate; }
@keyframes plDrift { from { transform: scale(1.06) translate3d(0, 0, 0); } to { transform: scale(1.14) translate3d(-1.5%, -1%, 0); } }
.pl-hero-shade {
  position: absolute; inset: 0;
  background:
    linear-gradient(95deg, rgba(3, 16, 10, 0.97) 0%, rgba(3, 16, 10, 0.82) 38%, rgba(3, 16, 10, 0.3) 75%, rgba(3, 16, 10, 0.55) 100%),
    linear-gradient(180deg, rgba(3, 16, 10, 0.6) 0%, rgba(3, 16, 10, 0) 22%, rgba(3, 16, 10, 0) 68%, var(--bg) 100%);
}

/* Ambient hero orbs */
.pl-hero-orb { position: absolute; border-radius: 50%; pointer-events: none; }
.pl-hero-orb--1 { width: 600px; height: 600px; top: -150px; right: 5%; background: radial-gradient(circle, rgba(34,197,94,0.14) 0%, transparent 65%); filter: blur(60px); animation: plOrbDrift1 20s ease-in-out infinite alternate; }
.pl-hero-orb--2 { width: 400px; height: 400px; bottom: 5%; right: 30%; background: radial-gradient(circle, rgba(214,185,123,0.12) 0%, transparent 65%); filter: blur(70px); animation: plOrbDrift2 25s ease-in-out infinite alternate; }
@keyframes plOrbDrift1 { from { transform: translate(0,0); } to { transform: translate(-40px,30px); } }
@keyframes plOrbDrift2 { from { transform: translate(0,0); } to { transform: translate(30px,-40px); } }
/* Letterbox bars slide away on load */
.pl-letterbox { position: absolute; left: 0; right: 0; height: 18vh; background: #000; z-index: 3; animation: plOpen 1.6s cubic-bezier(0.7, 0, 0.2, 1) 0.2s forwards; }
.pl-letterbox--top { top: 0; }
.pl-letterbox--bottom { bottom: 0; animation-name: plOpenDown; }
@keyframes plOpen { to { transform: translateY(-100%); } }
@keyframes plOpenDown { to { transform: translateY(100%); } }

.pl-vf { position: absolute; inset: 100px clamp(16px, 3vw, 40px) 32px; pointer-events: none; z-index: 1; opacity: 0.8; }
.pl-vf-c { position: absolute; width: 28px; height: 28px; border: 0 solid rgba(233, 245, 238, 0.55); }
.pl-vf-c--tl { top: 0; left: 0; border-top-width: 1px; border-left-width: 1px; }
.pl-vf-c--tr { top: 0; right: 0; border-top-width: 1px; border-right-width: 1px; }
.pl-vf-c--bl { bottom: 0; left: 0; border-bottom-width: 1px; border-left-width: 1px; }
.pl-vf-c--br { bottom: 0; right: 0; border-bottom-width: 1px; border-right-width: 1px; }
.pl-vf-rec, .pl-vf-tc {
  position: absolute; top: 12px; display: inline-flex; align-items: center; gap: 7px;
  font-family: "Inter", sans-serif; font-size: 11.5px; letter-spacing: 1.5px; color: rgba(233, 245, 238, 0.75);
}
.pl-vf-rec { left: 44px; }
.pl-vf-tc { right: 44px; }
.pl-vf-rec i { width: 7px; height: 7px; border-radius: 50%; background: #ef4444; box-shadow: 0 0 10px #ef4444; animation: plBlink 1.4s steps(2, start) infinite; }
@keyframes plBlink { to { visibility: hidden; } }

.pl-hero-inner {
  position: relative; z-index: 2; width: 100%; max-width: 1760px; margin: 0 auto;
  padding: 0 clamp(32px, 6vw, 96px);
  display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(0, 0.85fr); gap: clamp(32px, 5vw, 80px); align-items: center;
}
.pl-hero-copy > * { animation: plRise 1.1s cubic-bezier(0.2, 0.7, 0.2, 1) both; }
.pl-hero-copy > *:nth-child(1) { animation-delay: 0.9s; }
.pl-hero-copy > *:nth-child(2) { animation-delay: 1s; }
.pl-hero-copy > *:nth-child(3) { animation-delay: 1.1s; }
.pl-hero-copy > *:nth-child(4) { animation-delay: 1.2s; }
.pl-hero-copy > *:nth-child(5) { animation-delay: 1.3s; }
@keyframes plRise { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }

.pl-eyebrow { display: flex; align-items: center; gap: 14px; margin-bottom: 28px; font-size: 11.5px; font-weight: 600; letter-spacing: 2.6px; text-transform: uppercase; color: var(--gold); }
.pl-eyebrow-line { width: 44px; height: 1px; background: linear-gradient(90deg, var(--gold), transparent); }
.pl-title {
  font-family: "Inter", sans-serif; font-weight: 500;
  font-size: clamp(42px, 5.1vw, 78px); line-height: 1.04; letter-spacing: -1.2px;
  margin: 0 0 26px; color: #f4faf6;
  text-shadow: 0 2px 40px rgba(0,0,0,0.5);
}
.pl-title em { font-weight: 500; }
.pl-lead { max-width: 540px; margin: 0 0 38px; font-size: 18px; font-weight: 300; line-height: 1.75; color: rgba(233, 245, 238, 0.8); }
.pl-cta { display: flex; flex-wrap: wrap; gap: 14px; }
.pl-cta--center { justify-content: center; }
.pl-trust { display: flex; flex-wrap: wrap; gap: 0; margin: 48px 0 0; }
.pl-trust > div { padding: 0 28px; border-left: 1px solid var(--line-strong); }
.pl-trust > div:first-child { padding-left: 0; border-left: none; }
.pl-trust dt { font-family: "Inter", sans-serif; font-size: 26px; font-weight: 500; color: var(--ink); }
.pl-trust dd { margin: 2px 0 0; font-size: 12px; letter-spacing: 1.2px; text-transform: uppercase; color: var(--muted); }

/* Permit pass */
.pl-pass {
  position: relative; padding: 26px 26px 22px; border-radius: 24px;
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.04) 100%);
  border: 1px solid rgba(233, 245, 238, 0.18);
  backdrop-filter: blur(24px) saturate(140%); -webkit-backdrop-filter: blur(24px) saturate(140%);
  box-shadow: 0 40px 90px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.18);
  animation: plRise 1.2s cubic-bezier(0.2, 0.7, 0.2, 1) 1.3s both, plFloat 8s ease-in-out 2.5s infinite alternate;
}
.pl-pass::before { content: ""; position: absolute; inset: 0; border-radius: 24px; padding: 1px; background: linear-gradient(140deg, rgba(214, 185, 123, 0.7), transparent 40%, transparent 65%, rgba(74, 222, 128, 0.5)); -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0); -webkit-mask-composite: xor; mask-composite: exclude; pointer-events: none; }
@keyframes plFloat { from { transform: translateY(0); } to { transform: translateY(-10px); } }
.pl-pass-head { display: flex; align-items: center; gap: 12px; margin-bottom: 22px; }
.pl-pass-emblem { width: 40px; height: 40px; border-radius: 50%; background: #fff; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.pl-pass-emblem img { width: 76%; height: 76%; object-fit: contain; }
.pl-pass-head > span:nth-child(2) { display: flex; flex-direction: column; line-height: 1.25; }
.pl-pass-kicker { font-size: 10.5px; font-weight: 600; letter-spacing: 2.2px; text-transform: uppercase; color: var(--gold); }
.pl-pass-org { font-size: 13px; font-weight: 600; color: var(--ink); }
.pl-pass-live { margin-left: auto; display: inline-flex; align-items: center; gap: 6px; padding: 5px 11px; border-radius: 999px; background: rgba(74, 222, 128, 0.12); border: 1px solid rgba(74, 222, 128, 0.3); color: var(--emerald-soft); font-size: 11.5px; font-weight: 600; }
.pl-pass-live i { width: 6px; height: 6px; border-radius: 50%; background: var(--emerald-soft); animation: plPulse 1.8s infinite; }
@keyframes plPulse { 0% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.6); } 100% { box-shadow: 0 0 0 8px rgba(74, 222, 128, 0); } }
.pl-pass-title { font-family: "Inter", sans-serif; font-size: 27px; font-weight: 500; line-height: 1.2; margin-bottom: 20px; }
.pl-pass-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1px; background: var(--line); border: 1px solid var(--line); border-radius: 14px; overflow: hidden; }
.pl-pass-grid > div { display: flex; flex-direction: column; gap: 3px; padding: 12px 14px; background: rgba(3, 16, 10, 0.55); }
.pl-pass-grid span { font-size: 10.5px; letter-spacing: 1.4px; text-transform: uppercase; color: var(--muted); }
.pl-pass-grid b { font-size: 14.5px; font-weight: 600; color: var(--ink); }
.pl-pass-perf { position: relative; height: 1px; margin: 22px -26px 18px; background-image: linear-gradient(90deg, rgba(233, 245, 238, 0.3) 50%, transparent 50%); background-size: 10px 1px; }
.pl-pass-steps { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px; }
.pl-pass-steps li { display: flex; align-items: center; gap: 9px; font-size: 13px; color: rgba(233, 245, 238, 0.72); }
.pl-pass-dot { width: 9px; height: 9px; border-radius: 50%; border: 1px solid rgba(233, 245, 238, 0.45); flex-shrink: 0; }
.pl-pass-steps li.is-done { color: var(--ink); }
.pl-pass-steps li.is-done .pl-pass-dot { background: var(--emerald-soft); border-color: var(--emerald-soft); box-shadow: 0 0 10px rgba(74, 222, 128, 0.7); }

.pl-scroll { position: absolute; left: 50%; bottom: 30px; z-index: 2; transform: translateX(-50%); width: 24px; height: 38px; border-radius: 14px; border: 1px solid rgba(233, 245, 238, 0.4); }
.pl-scroll span { position: absolute; left: 50%; top: 8px; width: 3px; height: 8px; margin-left: -1.5px; border-radius: 2px; background: var(--ink); animation: plWheel 1.8s ease-in-out infinite; }
@keyframes plWheel { 0% { opacity: 1; transform: translateY(0); } 100% { opacity: 0; transform: translateY(12px); } }

/* ─── Marquee ─── */
.pl-marquee { overflow: hidden; padding: 28px 0; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); background: var(--bg-2); position: relative; }
.pl-marquee::before, .pl-marquee::after { content: ""; position: absolute; top: 0; bottom: 0; width: 120px; z-index: 1; pointer-events: none; }
.pl-marquee::before { left: 0; background: linear-gradient(90deg, var(--bg-2), transparent); }
.pl-marquee::after { right: 0; background: linear-gradient(270deg, var(--bg-2), transparent); }
.pl-marquee-track { display: flex; width: max-content; animation: plMarquee 48s linear infinite; }
.pl-marquee:hover .pl-marquee-track { animation-play-state: paused; }
@keyframes plMarquee { to { transform: translateX(-50%); } }
.pl-marquee-item { display: inline-flex; align-items: center; white-space: nowrap; font-family: "Inter", sans-serif; font-style: italic; font-size: clamp(22px, 2.6vw, 32px); color: rgba(233, 245, 238, 0.85); }
.pl-marquee-item i { font-style: normal; margin: 0 36px; font-size: 14px; color: var(--gold); }

/* ─── Sections ─── */
.pl-section { position: relative; padding: clamp(80px, 11vw, 140px) 0; }
.pl-section--tint { background: linear-gradient(180deg, var(--bg-2) 0%, var(--bg) 100%); border-top: 1px solid var(--line); }
.pl-inner { max-width: 1760px; margin: 0 auto; padding: 0 clamp(16px, 4vw, 48px); }
.pl-head { max-width: 760px; margin: 0 auto 64px; text-align: center; }
.pl-tag { display: inline-flex; align-items: center; gap: 12px; margin-bottom: 20px; font-size: 11.5px; font-weight: 600; letter-spacing: 2.8px; text-transform: uppercase; color: var(--gold); }
.pl-tag::before { content: ""; width: 28px; height: 1px; background: var(--gold); opacity: 0.7; }
.pl-tag--light { color: #ead9b0; }
.pl-h2 { font-family: "Inter", sans-serif; font-weight: 500; font-size: clamp(34px, 4.4vw, 58px); line-height: 1.1; letter-spacing: -0.8px; margin: 0 0 18px; color: #f4faf6; }
.pl-sub { margin: 0; font-size: 16.5px; font-weight: 300; line-height: 1.8; color: var(--muted); }
.pl-sub--left { margin-bottom: 28px; }

/* The Portal: editorial two-column */
.pl-craft { display: grid; grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr); gap: clamp(40px, 7vw, 110px); align-items: start; }
.pl-craft-head { position: sticky; top: 120px; }
.pl-craft-head .pl-sub { margin-bottom: 30px; }
.pl-craft-list { list-style: none; margin: 0; padding: 0; border-top: 1px solid var(--line); }
.pl-craft-item { display: grid; grid-template-columns: 48px 48px 1fr; gap: 18px; align-items: start; padding: 28px 8px; border-bottom: 1px solid var(--line); transition: background 0.35s ease, padding-left 0.3s ease; }
.pl-craft-item:hover { background: linear-gradient(90deg, rgba(74, 222, 128, 0.08), rgba(74,222,128,0.02) 50%, transparent 80%); padding-left: 16px; }
.pl-craft-no { padding-top: 10px; font-family: "Inter", sans-serif; font-size: 18px; color: var(--gold); }
.pl-craft-icon { width: 48px; height: 48px; border-radius: 14px; border: 1px solid var(--line-strong); display: flex; align-items: center; justify-content: center; background: var(--card); }
.pl-craft-body { display: flex; flex-direction: column; gap: 6px; padding-top: 2px; }
.pl-craft-title { font-size: 19px; font-weight: 600; color: var(--ink); }
.pl-craft-desc { font-size: 15px; font-weight: 300; line-height: 1.7; color: var(--muted); }

/* Process */
.pl-steps { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(4, 1fr); gap: 28px; position: relative; }
.pl-steps::before { content: ""; position: absolute; top: 72px; left: 12.5%; right: 12.5%; height: 1px; background: linear-gradient(90deg, transparent, rgba(214, 185, 123, 0.6), rgba(74, 222, 128, 0.5), transparent); }
.pl-step { position: relative; text-align: center; padding: 0 8px; }
.pl-step-no { display: block; margin-bottom: 18px; font-size: 11px; font-weight: 600; letter-spacing: 2.6px; text-transform: uppercase; color: var(--muted); }
.pl-step-ring { position: relative; z-index: 1; width: 64px; height: 64px; margin: 0 auto 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: var(--bg); border: 1px solid rgba(214, 185, 123, 0.55); box-shadow: 0 0 0 8px var(--bg), 0 0 30px rgba(74, 222, 128, 0.18); transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease; }
.pl-step:hover .pl-step-ring { transform: translateY(-5px); border-color: var(--emerald-soft); box-shadow: 0 0 0 8px var(--bg), 0 0 40px rgba(74, 222, 128, 0.35), 0 0 80px rgba(74,222,128,0.12); }
.pl-step-title { font-family: "Inter", sans-serif; font-size: 22px; font-weight: 500; margin: 0 0 8px; }
.pl-step-desc { margin: 0; font-size: 14.5px; font-weight: 300; line-height: 1.7; color: var(--muted); }

/* Posters */
.pl-posters { display: grid; grid-template-columns: repeat(4, 1fr); gap: 22px; }
.pl-poster { border-radius: 20px; overflow: hidden; border: 1px solid var(--line); background: var(--card); transition: transform 0.45s cubic-bezier(0.2, 0.7, 0.2, 1), box-shadow 0.45s ease, border-color 0.45s ease; }
.pl-poster:hover { transform: translateY(-10px) scale(1.02); border-color: rgba(74,222,128,0.35); box-shadow: 0 30px 70px rgba(0, 0, 0, 0.6), 0 0 30px rgba(74,222,128,0.12); }
.pl-poster-art { position: relative; aspect-ratio: 4 / 4.2; display: flex; align-items: center; justify-content: center; overflow: hidden; }
.pl-poster-art::after { content: ""; position: absolute; inset: 0; background: linear-gradient(180deg, transparent 50%, rgba(3, 16, 10, 0.75)); }
.pl-poster-art .v-icon { transition: transform 0.6s ease; }
.pl-poster:hover .pl-poster-art .v-icon { transform: scale(1.12); }
.pl-poster-badge { position: absolute; top: 16px; left: 16px; z-index: 1; padding: 4px 10px; border-radius: 999px; border: 1px solid rgba(214, 185, 123, 0.6); color: #ead9b0; font-size: 10.5px; font-weight: 600; letter-spacing: 1.6px; text-transform: uppercase; background: rgba(3, 16, 10, 0.45); backdrop-filter: blur(6px); }
.pl-poster-index { position: absolute; right: 18px; bottom: 12px; z-index: 1; font-family: "Inter", sans-serif; font-style: italic; font-size: 40px; color: rgba(233, 245, 238, 0.22); }
.pl-poster-body { padding: 22px 22px 24px; }
.pl-poster-title { font-family: "Inter", sans-serif; font-size: 23px; font-weight: 500; margin: 0 0 8px; }
.pl-poster-desc { margin: 0 0 16px; font-size: 14px; font-weight: 300; line-height: 1.65; color: var(--muted); }
.pl-poster-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.pl-poster-tags span { padding: 4px 10px; border-radius: 999px; border: 1px solid var(--line-strong); font-size: 11.5px; color: rgba(233, 245, 238, 0.78); }

/* Statement */
.pl-about { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: clamp(40px, 7vw, 100px); align-items: center; }
.pl-about-media { position: relative; z-index: 0; margin: 0; }
.pl-about-frame { position: relative; border-radius: 22px; overflow: hidden; box-shadow: 0 40px 80px rgba(0, 0, 0, 0.5); }
.pl-about-frame::after { content: ""; position: absolute; inset: 0; background: linear-gradient(180deg, transparent 55%, rgba(3, 16, 10, 0.55)); }
.pl-about-frame img { display: block; width: 100%; aspect-ratio: 7 / 5.4; object-fit: cover; }
.pl-about-media::before { content: ""; position: absolute; inset: 24px -24px -24px 24px; border: 1px solid rgba(214, 185, 123, 0.45); border-radius: 22px; z-index: -1; }
.pl-about-cap { position: absolute; left: 22px; bottom: 22px; z-index: 1; display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; border-radius: 999px; background: linear-gradient(180deg, #4ade80, #22c55e); color: #03140a; font-size: 13px; font-weight: 600; }
.pl-quote { margin: 0 0 22px; font-family: "Inter", sans-serif; font-style: italic; font-weight: 500; font-size: clamp(30px, 3.6vw, 46px); line-height: 1.2; color: #f4faf6; }
.pl-points { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 14px; }
.pl-points li { display: flex; align-items: baseline; gap: 14px; font-size: 15.5px; font-weight: 300; line-height: 1.65; color: rgba(233, 245, 238, 0.82); }
.pl-points strong { font-weight: 600; color: var(--ink); }
.pl-point-mark { width: 8px; height: 8px; flex-shrink: 0; transform: rotate(45deg); background: var(--gold); }

/* Final CTA */
.pl-final { position: relative; padding: clamp(110px, 15vw, 190px) 16px; text-align: center; overflow: hidden; }
.pl-final-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.pl-final-shade { position: absolute; inset: 0; background: radial-gradient(ellipse at center, rgba(3, 16, 10, 0.62) 0%, rgba(3, 16, 10, 0.94) 72%); }
.pl-final-inner { position: relative; z-index: 1; max-width: 760px; margin: 0 auto; }
.pl-final-title { font-family: "Inter", sans-serif; font-weight: 500; font-size: clamp(42px, 6vw, 78px); line-height: 1.05; letter-spacing: -1px; margin: 0 0 18px; color: #f4faf6; }
.pl-final-sub { margin: 0 auto 38px; max-width: 560px; font-size: 17px; font-weight: 300; line-height: 1.75; color: rgba(233, 245, 238, 0.8); }

/* Credits */
.pl-credits { padding: 72px 0 40px; text-align: center; background: #010805; border-top: 1px solid transparent; border-image: linear-gradient(90deg, transparent, rgba(214,185,123,0.4), rgba(74,222,128,0.3), rgba(214,185,123,0.4), transparent) 1; }
.pl-credits-brand { display: inline-flex; align-items: center; gap: 12px; margin-bottom: 34px; }
.pl-credits-roll { display: flex; justify-content: center; flex-wrap: wrap; gap: 24px 72px; margin: 0 0 36px; }
.pl-credits-roll dt { margin-bottom: 6px; font-size: 10.5px; font-weight: 600; letter-spacing: 2.6px; text-transform: uppercase; color: var(--gold); }
.pl-credits-roll dd { margin: 0; font-family: "Inter", sans-serif; font-size: 20px; color: var(--ink); }
.pl-credits-links { display: flex; justify-content: center; flex-wrap: wrap; gap: 28px; margin-bottom: 28px; }
.pl-credits-links a { color: var(--muted); font-size: 14px; transition: color 0.2s; }
.pl-credits-links a:hover { color: var(--emerald-soft); }
.pl-credits-copy { margin: 0; font-size: 12.5px; color: rgba(157, 179, 166, 0.65); }

/* Reveal on scroll */
.reveal { opacity: 0; transform: translateY(30px); transition: opacity 0.9s ease, transform 0.9s cubic-bezier(0.2, 0.7, 0.2, 1), background 0.35s ease, border-color 0.45s ease, box-shadow 0.45s ease; }
.reveal.reveal--in { opacity: 1; transform: none; }
.pl-poster.reveal--in:hover { transform: translateY(-8px); }

/* ─── Responsive ─── */
@media (max-width: 1100px) {
  .pl-posters { grid-template-columns: repeat(2, 1fr); }
  .pl-steps { grid-template-columns: repeat(2, 1fr); row-gap: 48px; }
  .pl-steps::before { display: none; }
}
@media (max-width: 960px) {
  .pl-hero-inner { grid-template-columns: 1fr; }
  .pl-craft, .pl-about { grid-template-columns: 1fr; }
  .pl-craft-head { position: static; }
  .pl-about-media::before { display: none; }
}
@media (max-width: 760px) {
  .pl-nav-link, .pl-nav-sep { display: none; }
  .pl-hero { padding: 104px 0 84px; }
  .pl-hero-inner { padding: 0 28px; }
  .pl-vf { inset: 86px 10px 18px; }
  .pl-vf-rec { left: 36px; }
  .pl-vf-tc { right: 36px; }
  .pl-lead { font-size: 16px; }
  .pl-trust > div { padding: 0 16px; }
  .pl-trust dt { font-size: 21px; }
  .pl-craft-item { grid-template-columns: 36px 1fr; }
  .pl-craft-icon { display: none; }
}
@media (max-width: 600px) {
  .pl-posters, .pl-steps { grid-template-columns: 1fr; }
  .pl-brand-sub { display: none; }
  .pl-cta .pl-btn { flex: 1 1 100%; }
  .pl-trust { margin-top: 36px; display: grid; grid-template-columns: repeat(3, auto); justify-content: start; }
  .pl-trust > div { padding: 0 12px; }
  .pl-trust dt { font-size: 18px; }
  .pl-trust dd { font-size: 10px; letter-spacing: 0.8px; }
}
@media (prefers-reduced-motion: reduce) {
  .pl-hero-img, .pl-letterbox, .pl-hero-copy > *, .pl-pass, .pl-marquee-track, .pl-vf-rec i, .pl-pass-live i, .pl-scroll span { animation: none; }
  .pl-letterbox { display: none; }
  .reveal { opacity: 1; transform: none; transition: none; }
}
</style>
